import { useLocalStorage } from 'react-use';
import { lazy, startTransition, Suspense, useCallback, useMemo } from 'react';
import { cva } from 'class-variance-authority';
import { type PendingUserAccountItem, PendingUserAccountItemType, type WithTestId } from '@pkgs/components/types/types';
import { useUserAccount } from '@pkgs/components/hooks/useUserAccount';
import { useSearchAlertInfo } from '@pkgs/components/hooks/useSearchAlertInfo';
import { useHasNoLocation } from '@pkgs/components/hooks/useHasNoLocation';
import { useCreateSearchAlertButtonClick } from '@pkgs/components/hooks/useCreateSearchAlertButtonClick';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { PENDING_USER_ACCOUNT_ITEM_KEY } from '@pkgs/components/consts/consts';
import { Trigger } from '@pkgs/components/components/UserAccountModal/UserAccountModal';
import { SearchAlertTooltip } from '@pkgs/components/components/SearchAlert/SearchAlertTooltip';
import BellPlus from '@pkgs/components/assets/icons/small/BellPlus.svg';
import { useAuth } from '@ev-tech/user-account-modules';
import { useModalState } from '@ev-tech/ui-core-components/Modal';
import { Button, type ButtonVariant, type ButtonSizes } from '@ev-tech/ui-core-components/Button';
import { type PageSkill, useRegisterPageSkill } from '@ev-tech/search-modules-chatbot';
import { type Side } from '@base-ui/react/internals/useAnchorPositioning';

import { SearchAlertUsageInformation } from './SearchAlertInformation';
import { type SearchAlertButtonContext } from './SearchAlert.types';
import { SearchAlertSignInCard } from '../SearchAlertSignInCard/SearchAlertSignInCard';

import styles from './SearchAlert.module.css';

const searchAlertButtonVariants = cva(styles.searchAlertButton, {
  variants: {
    context: {
      'filter-bar': null,
      'sticky-footer': styles.stickyFooterButton,
    },
  },
});

const CreateSearchAlertModal = lazy(() => import('../SearchAlert/Index'));

export type SearchAlertButtonProps = WithTestId<{
  hasSearchAlertInfo?: boolean;
  context: SearchAlertButtonContext;
}>;

const getSkillDescription = (isSignedIn: boolean) =>
  isSignedIn
    ? 'Search alerts notify the user when new listings match the criteria. Will open a modal to save the search alert.'
    : 'Search alerts notify the user when new listings match the criteria. The user is not signed in, so this will show a sign in card.';

export const SearchAlertButtonComponent = ({
  dataTestId,
  context,
  setShouldShowSearchAlertInfo,
  variant,
  size,
  tooltipPlacement,
}: {
  dataTestId?: string;
  context?: SearchAlertButtonContext;
  setShouldShowSearchAlertInfo: (shouldShowSearchAlertInfo: boolean) => void;
  variant?: ButtonVariant;
  size?: ButtonSizes;
  tooltipPlacement?: Side;
}) => {
  const { t, featureFlags, ImageComponent, userAccountProps } = useAppContext();
  const searchAlertModalState = useModalState();

  const [, setPendingItemToSave] = useLocalStorage<PendingUserAccountItem | null>(PENDING_USER_ACCOUNT_ITEM_KEY);

  const { openUserAccountModal } = useUserAccount();
  const { isSignedIn } = useAuth();

  const hasNoLocation = useHasNoLocation();
  const promptValue = usePromptStore((state) => state.promptValue);
  const searchMode = useSearchStore((state) => state.searchMode);
  const { isPending } = useAiSearch();

  const { getSignInModalTrackingContexts, trackButtonClickUserInteractionEvent } = useCreateSearchAlertButtonClick();

  const isDisabled =
    (searchMode === SearchMode.CLASSIC && hasNoLocation) ||
    (featureFlags.isAiSearchEnabled && searchMode === SearchMode.AI && (!promptValue || isPending));
  const triggerId = `${context}_primary_search-alert`;

  const handleClick = useCallback(() => {
    trackButtonClickUserInteractionEvent(triggerId);
    startTransition(() => setShouldShowSearchAlertInfo(false));

    if (isSignedIn) {
      searchAlertModalState.open();
    } else {
      openUserAccountModal({
        signInTrigger: Trigger.SavedSearch,
        triggerId,
        additionalTrackingContexts: getSignInModalTrackingContexts(),
      });
      setPendingItemToSave({ type: PendingUserAccountItemType.SEARCH_ALERT });
    }
  }, [
    triggerId,
    isSignedIn,
    searchAlertModalState,
    openUserAccountModal,
    getSignInModalTrackingContexts,
    setPendingItemToSave,
    setShouldShowSearchAlertInfo,
    trackButtonClickUserInteractionEvent,
  ]);

  const searchAlertSkill = useMemo<PageSkill>(
    () => ({
      type: 'search-alert',
      description: getSkillDescription(isSignedIn ?? false),
      ...(isSignedIn
        ? { execute: handleClick }
        : {
            component: () => (
              <SearchAlertSignInCard t={userAccountProps.translationResolver} ImageComponent={ImageComponent} onClick={handleClick} />
            ),
          }),
    }),
    [isSignedIn, handleClick, userAccountProps, ImageComponent],
  );

  useRegisterPageSkill(searchAlertSkill);

  return (
    <SearchAlertTooltip side={tooltipPlacement ?? (context === 'filter-bar' ? 'bottom' : 'top')}>
      <Button
        icon={<BellPlus />}
        variant={variant ?? 'primary'}
        size={size ?? (context === 'filter-bar' ? 'small' : 'medium')}
        label={t('search.filterbar.searchAlert.button.label')}
        disabled={isDisabled}
        onClick={handleClick}
        testId={dataTestId ? `${dataTestId}_desktop` : undefined}
        className={searchAlertButtonVariants({ context })}
      />
      {searchAlertModalState.isOpen && (
        <Suspense>
          <CreateSearchAlertModal modalState={searchAlertModalState} />
        </Suspense>
      )}
    </SearchAlertTooltip>
  );
};

export const SearchAlertButton = ({ 'data-testid': dataTestId, context }: SearchAlertButtonProps) => {
  const [shouldShowSearchAlertInfo, setShouldShowSearchAlertInfo] = useSearchAlertInfo();

  return (
    <SearchAlertUsageInformation shouldShowSearchAlertInfo={shouldShowSearchAlertInfo} onClose={() => setShouldShowSearchAlertInfo(false)}>
      <SearchAlertButtonComponent setShouldShowSearchAlertInfo={setShouldShowSearchAlertInfo} dataTestId={dataTestId} context={context} />
    </SearchAlertUsageInformation>
  );
};
